import React from 'react';
import { Check, X } from 'lucide-react';

/**
 * PhaseGate — explicit gate-validation card for Build → Decide → Act
 * advancement.
 *
 * Per Design Principles §5.6 + §3.7, every phase tab in the Agentlet
 * Detail page leads with this card. It lists every gate criterion with
 * a green ✓ or red ✗ and a remediation link for any failed criterion.
 * The "Advance to next phase" button is disabled until all criteria
 * pass — never silent gating.
 *
 * @param {Object} props
 * @param {'BUILD'|'DECIDE'|'ACT'} props.phase
 * @param {Array<{
 *   id: string,
 *   label: React.ReactNode,
 *   passed: boolean,
 *   detail?: React.ReactNode,
 *   remediation?: { label: string, href?: string, onClick?: () => void },
 * }>} props.criteria
 * @param {React.ReactNode} [props.advanceAction]
 *   Optional button slot. Caller decides what enable/disable looks like;
 *   typically a v2 Button bound to an "advance" handler. Pass null to
 *   suppress the action row entirely.
 * @param {string} [props.title]
 *   Override the auto-generated title.
 */
export function PhaseGate({ phase, criteria, advanceAction, title, advanced }) {
  const passed = criteria.filter((c) => c.passed).length;
  const total = criteria.length;

  const PHASE_LABEL = {
    BUILD: 'Build',
    DECIDE: 'Decide',
    ACT: 'Act',
  };
  const PHASE_NEXT = {
    BUILD: 'Decide',
    DECIDE: 'Act',
    ACT: 'Published',
  };

  // When the agent has already advanced past this phase, the agent-level gate no
  // longer applies (new/regressed skills progress per-skill via each skill's Advance).
  // Render an "advanced" header instead of a stale "0 of 0 passing".
  const headerTitle = title || (advanced
    ? `${PHASE_LABEL[phase] || phase} → ${PHASE_NEXT[phase] || 'next'} · advanced`
    : `${PHASE_LABEL[phase] || phase} → ${PHASE_NEXT[phase] || 'next'} gate · ${passed} of ${total} passing`);

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm p-5">
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-v2-cyan bg-v2-cyan-soft rounded-v2-pill px-2 py-0.5">
          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-v2-cyan" />
          {PHASE_LABEL[phase] || phase}
        </span>
        <h3 className="text-[14px] font-bold m-0 text-v2-ink flex-1">{headerTitle}</h3>
        {advanceAction !== null && advanceAction}
      </div>
      <ul className="space-y-1.5 list-none p-0 m-0" role="list">
        {criteria.map((c) => (
          <li
            key={c.id}
            className={
              'flex items-start gap-3 text-[12.5px] leading-relaxed ' +
              'rounded-v2-sm px-3 py-2 ' +
              (c.passed ? 'bg-v2-muted text-v2-ink' : 'bg-v2-danger-soft text-v2-danger')
            }
          >
            <span
              aria-hidden="true"
              className={
                'flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-px ' +
                (c.passed
                  ? 'bg-v2-success-soft text-v2-success'
                  : 'bg-v2-surface text-v2-danger border border-v2-danger')
              }
            >
              {c.passed ? <Check size={10} /> : <X size={10} />}
            </span>
            <span className="flex-1 min-w-0">
              <span className={c.passed ? 'text-v2-ink' : 'text-v2-danger font-semibold'}>
                {c.label}
              </span>
              {c.detail && (
                <span className="block text-[11.5px] text-v2-ink-muted mt-0.5">{c.detail}</span>
              )}
            </span>
            {!c.passed && c.remediation && (
              c.remediation.href ? (
                <a
                  href={c.remediation.href}
                  className="v2-focus-ring text-[11.5px] font-semibold text-v2-action hover:text-v2-action-strong"
                >
                  {c.remediation.label} &rarr;
                </a>
              ) : (
                <button
                  type="button"
                  onClick={c.remediation.onClick}
                  className="v2-focus-ring text-[11.5px] font-semibold text-v2-action hover:text-v2-action-strong"
                >
                  {c.remediation.label} &rarr;
                </button>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PhaseGate;
